import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Mail, CheckCircle, XCircle, Eye, MousePointer, Users, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Kampanya verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { color: 'bg-green-100 text-green-700', icon: '✅', text: 'Başarılı' },
      partial: { color: 'bg-yellow-100 text-yellow-700', icon: '⚠️', text: 'Kısmi Başarılı' },
      failed: { color: 'bg-red-100 text-red-700', icon: '❌', text: 'Başarısız' }
    };
    
    const badge = badges[status] || badges.success;
    
    return (
      <span className={`${badge.color} px-3 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1`}>
        <span>{badge.icon}</span>
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Bugün ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Dün ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('tr-TR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  // Summary stats
  const totalCampaigns = campaigns.length;
  const totalSent = campaigns.reduce((sum, c) => sum + c.total_sent, 0);
  const totalDelivered = campaigns.reduce((sum, c) => sum + c.total_delivered, 0);
  const avgOpenRate = campaigns.length > 0 
    ? (campaigns.reduce((sum, c) => sum + c.open_rate, 0) / campaigns.length).toFixed(2)
    : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight">Kampanya Raporları</h1>
        <p className="text-lg text-[#64748b] mt-2">Gönderilen kampanyaların detaylı istatistikleri</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[#64748b]">Toplam Kampanya</p>
            <TrendingUp size={20} className="text-[#6366f1]" />
          </div>
          <p className="text-3xl font-extrabold text-[#0f172a]">{totalCampaigns}</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[#64748b]">Toplam Gönderilen</p>
            <Mail size={20} className="text-[#3b82f6]" />
          </div>
          <p className="text-3xl font-extrabold text-[#0f172a]">{totalSent}</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[#64748b]">Teslim Edilen</p>
            <CheckCircle size={20} className="text-[#10b981]" />
          </div>
          <p className="text-3xl font-extrabold text-[#10b981]">{totalDelivered}</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[#64748b]">Ort. Açılma</p>
            <Eye size={20} className="text-[#f59e0b]" />
          </div>
          <p className="text-3xl font-extrabold text-[#f59e0b]">{avgOpenRate}%</p>
        </div>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-[#e2e8f0]">
          <Mail size={64} className="mx-auto text-[#cbd5e1] mb-4" />
          <h3 className="text-xl font-bold text-[#0f172a] mb-2">Henüz kampanya yok</h3>
          <p className="text-[#64748b]">İlk kampanyanızı gönderin ve burada istatistikleri görün!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <div 
              key={campaign.campaign_id} 
              className="bg-white rounded-xl border border-[#e2e8f0] p-6 hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-[#0f172a]">{campaign.template_name}</h3>
                    {getStatusBadge(campaign.status)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#64748b]">
                    <Calendar size={16} />
                    <span>{formatDate(campaign.sent_at)}</span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
                {/* Total Sent */}
                <div className="bg-[#f8fafc] p-4 rounded-lg border border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail size={16} className="text-[#6366f1]" />
                    <p className="text-xs font-medium text-[#64748b]">Gönderilen</p>
                  </div>
                  <p className="text-2xl font-extrabold text-[#0f172a]">{campaign.total_sent}</p>
                </div>

                {/* Delivered */}
                <div className="bg-[#f0fdf4] p-4 rounded-lg border border-[#bbf7d0]">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-[#10b981]" />
                    <p className="text-xs font-medium text-[#065f46]">Teslim Edilen</p>
                  </div>
                  <p className="text-2xl font-extrabold text-[#10b981]">{campaign.total_delivered}</p>
                  <p className="text-xs text-[#065f46] mt-1">{campaign.delivery_rate}%</p>
                </div>

                {/* Failed */}
                <div className="bg-[#fef2f2] p-4 rounded-lg border border-[#fecaca]">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle size={16} className="text-[#ef4444]" />
                    <p className="text-xs font-medium text-[#991b1b]">Başarısız</p>
                  </div>
                  <p className="text-2xl font-extrabold text-[#ef4444]">{campaign.total_failed}</p>
                  <p className="text-xs text-[#991b1b] mt-1">{campaign.failure_rate}%</p>
                </div>

                {/* Opened */}
                <div className="bg-[#fef3c7] p-4 rounded-lg border border-[#fde68a]">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye size={16} className="text-[#f59e0b]" />
                    <p className="text-xs font-medium text-[#92400e]">Açılan</p>
                  </div>
                  <p className="text-2xl font-extrabold text-[#f59e0b]">{campaign.total_opened}</p>
                  <p className="text-xs text-[#92400e] mt-1">{campaign.open_rate}%</p>
                </div>

                {/* Clicked */}
                <div className="bg-[#dbeafe] p-4 rounded-lg border border-[#bfdbfe]">
                  <div className="flex items-center gap-2 mb-2">
                    <MousePointer size={16} className="text-[#3b82f6]" />
                    <p className="text-xs font-medium text-[#1e40af]">Tıklanan</p>
                  </div>
                  <p className="text-2xl font-extrabold text-[#3b82f6]">{campaign.total_clicked}</p>
                  <p className="text-xs text-[#1e40af] mt-1">{campaign.click_rate}%</p>
                </div>

                {/* Recipients */}
                <div className="bg-[#f5f3ff] p-4 rounded-lg border border-[#e9d5ff]">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-[#8b5cf6]" />
                    <p className="text-xs font-medium text-[#5b21b6]">Alıcılar</p>
                  </div>
                  <p className="text-2xl font-extrabold text-[#8b5cf6]">{campaign.recipients.length}</p>
                  <p className="text-xs text-[#5b21b6] mt-1">Kişi</p>
                </div>
              </div>

              {/* Performance Indicator */}
              <div className="mt-4 pt-4 border-t border-[#e2e8f0]">
                {campaign.open_rate >= 20 && (
                  <div className="flex items-center gap-2 text-sm text-[#10b981]">
                    <CheckCircle size={16} />
                    <span className="font-medium">Mükemmel performans! Açılma oranı hedefe ulaştı.</span>
                  </div>
                )}
                {campaign.open_rate >= 10 && campaign.open_rate < 20 && (
                  <div className="flex items-center gap-2 text-sm text-[#f59e0b]">
                    <AlertCircle size={16} />
                    <span className="font-medium">İyi performans. Açılma oranı hedefin altında ama yakın.</span>
                  </div>
                )}
                {campaign.open_rate < 10 && campaign.total_delivered > 0 && (
                  <div className="flex items-center gap-2 text-sm text-[#ef4444]">
                    <XCircle size={16} />
                    <span className="font-medium">Düşük açılma oranı. Konu başlığını ve içeriği iyileştirin.</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Campaigns;
